import { useEffect, useMemo, useState } from 'react';
import { Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Content,
  EmptyState,
  InfoCard,
  Progress,
  WarningPanel,
} from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';
import {
  RELATION_HAS_PART,
  parseEntityRef,
  type Entity,
} from '@backstage/catalog-model';
import { ReleaseList, type ReleaseListItem } from './ReleaseList';
import { formatRelativeDate } from './formatRelativeDate';
import { releasesApiRef } from './api';

const GITHUB_PROJECT_SLUG = 'github.com/project-slug';

/** How many releases the tab shows, and how many "Show more" adds. */
const RELEASE_PAGE_SIZE = 5;

/**
 * Most repositories one system's tab reads. Each is one request to the
 * per-repo route, so a very large system is capped rather than fanned out.
 */
export const MAX_SYSTEM_REPOS = 20;

/** Asks the per-repo route for everything it holds; it caps the answer. */
const REPO_REQUEST_LIMIT = 50;

/** A repository the tab reads, and the entity its releases are shown under. */
export interface ReleaseSource {
  slug: string;
  title: string;
}

const useStyles = makeStyles({
  cardActions: {
    justifyContent: 'center',
  },
});

function projectSlugOf(entity: Entity): string | undefined {
  const slug = entity.metadata.annotations?.[GITHUB_PROJECT_SLUG]?.trim();
  if (!slug) return undefined;
  const [owner, repo, ...rest] = slug.split('/');
  return owner && repo && rest.length === 0 ? `${owner}/${repo}` : undefined;
}

/** The refs of the Components a system lists as its parts. */
export function systemComponentRefs(entity: {
  kind?: string;
  metadata: { annotations?: Record<string, string> };
  relations?: { type: string; targetRef: string }[];
}): string[] {
  return (entity.relations ?? [])
    .filter(
      relation =>
        relation.type === RELATION_HAS_PART &&
        parseEntityRef(relation.targetRef).kind.toLowerCase() === 'component',
    )
    .map(relation => relation.targetRef);
}

/**
 * The repositories a system's tab reads: the system's own repo first, then
 * each component's, one entry per repo (the first entity to name a repo
 * labels it), capped at `MAX_SYSTEM_REPOS`.
 */
export function systemReleaseSources(
  system: Entity,
  components: Entity[],
): ReleaseSource[] {
  const sources = new Map<string, ReleaseSource>();
  for (const entity of [system, ...components]) {
    const slug = projectSlugOf(entity);
    const key = slug?.toLowerCase();
    if (slug && key && !sources.has(key)) {
      sources.set(key, {
        slug,
        title: entity.metadata.title ?? entity.metadata.name,
      });
    }
  }
  return [...sources.values()].slice(0, MAX_SYSTEM_REPOS);
}

interface RollupState {
  releases: ReleaseListItem[];
  repoCount: number;
  failedRepoCount: number;
  fetchedAt?: string;
}

/**
 * A system's Releases tab: the newest releases of the system's own repo and
 * of every component it lists as a part, merged by date.
 *
 * Each repo is read through the per-repo route, which serves only repos a
 * catalog entity the viewer can read points at, so the roll-up shows nothing
 * the viewer could not reach one component at a time. A repo that fails is
 * counted and skipped, so one renamed repo does not blank the tab.
 */
export const SystemReleasesContent = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);
  const releasesApi = useApi(releasesApiRef);

  const componentRefs = useMemo(() => systemComponentRefs(entity), [entity]);

  const [state, setState] = useState<RollupState | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();
  const [shown, setShown] = useState(RELEASE_PAGE_SIZE);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(undefined);
      try {
        const components = componentRefs.length
          ? (
              await catalogApi.getEntitiesByRefs({
                entityRefs: componentRefs,
                fields: [
                  'kind',
                  'metadata.name',
                  'metadata.namespace',
                  'metadata.title',
                  'metadata.annotations',
                ],
              })
            ).items.filter((item): item is Entity => Boolean(item))
          : [];
        const sources = systemReleaseSources(entity, components);

        const results = await Promise.allSettled(
          sources.map(async source => {
            const [owner, repo] = source.slug.split('/');
            const body = await releasesApi.getRepoReleases(
              { owner, repo },
              { limit: REPO_REQUEST_LIMIT },
            );
            return {
              fetchedAt: body.fetchedAt,
              releases: body.releases.map(release => ({
                ...release,
                entityTitle: source.title,
              })),
            };
          }),
        );
        if (cancelled) return;

        const read = results.flatMap(result =>
          result.status === 'fulfilled' ? [result.value] : [],
        );
        const failedRepoCount = results.length - read.length;
        if (sources.length > 0 && read.length === 0) {
          throw new Error(
            `None of this system's ${sources.length} repositories could be read.`,
          );
        }

        setState({
          releases: read
            .flatMap(result => result.releases)
            .sort(
              (a, b) =>
                new Date(b.publishedAt).getTime() -
                new Date(a.publishedAt).getTime(),
            ),
          repoCount: sources.length,
          failedRepoCount,
          // The oldest read, so "Updated" never overstates freshness.
          fetchedAt: read.map(result => result.fetchedAt).sort()[0],
        });
      } catch (err: any) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [entity, componentRefs, catalogApi, releasesApi]);

  if (loading && !state) return <Progress />;

  if (error) {
    return (
      <Content>
        <WarningPanel title="Could not load releases" message={error.message} />
      </Content>
    );
  }

  if (!state || state.repoCount === 0) {
    return (
      <Content>
        <EmptyState
          missing="data"
          title="No repositories to read"
          description="Neither this system nor its components name a GitHub repository (github.com/project-slug)."
        />
      </Content>
    );
  }

  if (state.releases.length === 0) {
    return (
      <Content>
        <EmptyState
          missing="data"
          title="No releases yet"
          description={`None of this system's ${state.repoCount} repositories has published a GitHub release.`}
        />
      </Content>
    );
  }

  const age = state.fetchedAt ? formatRelativeDate(state.fetchedAt) : '';
  const visible = state.releases.slice(0, shown);
  const remaining = state.releases.length - visible.length;

  return (
    <Content>
      {state.failedRepoCount > 0 && (
        <WarningPanel
          severity="info"
          title={`${state.failedRepoCount} of ${state.repoCount} repositories could not be read`}
          message="Their releases are missing from this list."
        />
      )}
      <InfoCard
        title="Recent releases across this system"
        subheader={
          <span>
            From {state.repoCount}{' '}
            {state.repoCount === 1 ? 'repository' : 'repositories'}
            {age ? ` · updated ${age}` : ''}
            {remaining > 0
              ? ` · showing ${visible.length} of ${state.releases.length}`
              : ''}
          </span>
        }
        actionsClassName={classes.cardActions}
        actions={
          remaining > 0 ? (
            <Button
              color="primary"
              onClick={() => setShown(current => current + RELEASE_PAGE_SIZE)}
            >
              Show {Math.min(RELEASE_PAGE_SIZE, remaining)} more
            </Button>
          ) : undefined
        }
      >
        <ReleaseList releases={visible} />
      </InfoCard>
    </Content>
  );
};

export default SystemReleasesContent;
